import { create } from "mutative";
import { useMemo, useReducer, type PropsWithChildren } from "react";
import {
    defaultLayoutContext,
    LayoutActionsContext,
    LayoutContext,
    LayoutPermissionMode,
    type ILayoutActionsContext,
    type ILayoutContext,
    type ILayoutItemContext,
    type ILayoutLink,
    type ILayoutSubMenu,
} from "src/contexts/layouts/LayoutContext";
import type { Permission } from "src/api/Tmd.dtos";

type LayoutAction =
    | { type: "setMenuItems"; menuItems: ILayoutLink[] }
    | { type: "setSubMenus"; subMenus: ILayoutSubMenu[] }
    | { type: "addSubMenu"; subMenu: ILayoutSubMenu }
    | { type: "removeSubMenu"; subMenu: ILayoutSubMenu }
    | { type: "addSubMenuByCode"; code: string }
    | { type: "removeSubMenuByCode"; code: string }
    | { type: "setRestricted"; restricted: boolean }
    | { type: "setPermission"; permission: Permission }
    | { type: "setPermissionMode"; permissionMode: LayoutPermissionMode }
    | { type: "setLoading"; loading: boolean }
    | { type: "setContext"; context: ILayoutItemContext };

export function LayoutReducer(state: ILayoutContext, action: LayoutAction): ILayoutContext {
    return create(state, (draft) => {
        switch (action.type) {
            case "setMenuItems":
                draft.menuItems = action.menuItems;
                break;

            case "setSubMenus":
                draft.subMenus = action.subMenus;
                break;

            case "addSubMenu": {
                const subMenus = draft.subMenus ?? [];
                const existingIndex = subMenus.findIndex(
                    (subMenu) => subMenu.code === action.subMenu.code,
                );

                if (existingIndex >= 0) {
                    subMenus[existingIndex] = action.subMenu;
                } else {
                    subMenus.push(action.subMenu);
                }

                draft.subMenus = subMenus;
                break;
            }

            case "removeSubMenu":
                draft.subMenus = (draft.subMenus ?? []).filter(
                    (subMenu) => subMenu.code !== action.subMenu.code,
                );
                break;

            case "addSubMenuByCode":
                if (!(draft.subMenus ?? []).some((subMenu) => subMenu.code === action.code)) {
                    draft.subMenus = [
                        ...(draft.subMenus ?? []),
                        {
                            code: action.code,
                            items: [],
                        },
                    ];
                }
                break;

            case "removeSubMenuByCode":
                draft.subMenus = (draft.subMenus ?? []).filter(
                    (subMenu) => subMenu.code !== action.code,
                );
                break;

            case "setRestricted":
                draft.restricted = action.restricted;
                break;

            case "setPermission":
                draft.permission = action.permission;
                break;

            case "setPermissionMode":
                draft.permissionMode = action.permissionMode;
                break;

            case "setLoading":
                draft.loading = action.loading;
                break;

            case "setContext":
                draft.context = action.context;
                break;
        }
    });
}

interface ILayoutProviderProps extends PropsWithChildren {
    initialState?: ILayoutContext;
}

function LayoutProvider({ children, initialState }: ILayoutProviderProps) {
    const [layout, dispatch] = useReducer(LayoutReducer, initialState ?? defaultLayoutContext);

    const actions = useMemo<ILayoutActionsContext>(
        () => ({
            setMenuItems: (menuItems: ILayoutLink[]) =>
                dispatch({ type: "setMenuItems", menuItems }),
            setSubMenus: (subMenus: ILayoutSubMenu[]) =>
                dispatch({ type: "setSubMenus", subMenus }),
            addSubMenu: (subMenu: ILayoutSubMenu) => dispatch({ type: "addSubMenu", subMenu }),
            removeSubMenu: (subMenu: ILayoutSubMenu) =>
                dispatch({ type: "removeSubMenu", subMenu }),
            addSubMenuByCode: (code: string) => dispatch({ type: "addSubMenuByCode", code }),
            removeSubMenuByCode: (code: string) => dispatch({ type: "removeSubMenuByCode", code }),
            setRestricted: (restricted: boolean) => dispatch({ type: "setRestricted", restricted }),
            setPermission: (permission: Permission) =>
                dispatch({ type: "setPermission", permission }),
            setPermissionMode: (permissionMode: LayoutPermissionMode) =>
                dispatch({ type: "setPermissionMode", permissionMode }),
            setLoading: (loading: boolean) => dispatch({ type: "setLoading", loading }),
            setContext: (context: ILayoutItemContext) => dispatch({ type: "setContext", context }),
        }),
        [dispatch],
    );

    return (
        <LayoutContext.Provider value={layout}>
            <LayoutActionsContext.Provider value={actions}>
                {children}
            </LayoutActionsContext.Provider>
        </LayoutContext.Provider>
    );
}

export default LayoutProvider;
